import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  Snackbar,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
  Stack,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Info as InfoIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { format } from 'date-fns';

interface RecordingRule {
  id: number;
  custid: string;
  rule_name: string;
  rule_type: 'global' | 'did' | 'extension' | 'direction';
  rule_value?: string;
  action: 'record' | 'no_record';
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  usage_count?: number;
  last_used?: string;
}

export default function RecordingRules() {
  const { member, isLoginAsCustomer } = useAuthWithCustomerOverride();
  const customerFilter = useCustomerFilter();
  
  // Check for login-as-customer token and session data
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = sessionStorage.getItem('loginAsCustomerId') && sessionStorage.getItem('loginAsCustomerName');
  
  // Initialize waiting state based on token presence
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      console.log('[RecordingRules] Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });
  
  const [rules, setRules] = useState<RecordingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRule, setSelectedRule] = useState<RecordingRule | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });
  
  // Form states
  const [formData, setFormData] = useState({
    rule_name: '',
    rule_type: 'global' as 'global' | 'did' | 'extension' | 'direction',
    rule_value: '',
    action: 'record' as 'record' | 'no_record',
    priority: 100,
    is_active: true
  });
  
  // Fetch recording rules
  const fetchRules = useCallback(async () => {
    // Safety check - don't fetch if we're waiting for login-as data
    if (waitingForLoginAs) {
      console.log('[RecordingRules] Skipping fetch - waiting for login-as data');
      return;
    }
    
    // Verify customer context matches if in login-as mode
    const sessionCustomerId = sessionStorage.getItem('loginAsCustomerId');
    if (sessionCustomerId && customerFilter.customerId && 
        sessionCustomerId !== String(customerFilter.customerId)) {
      console.log('[RecordingRules] Customer mismatch - waiting for correct context');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {};
      // Check login as customer first
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && customerFilter.customerId) {
        params.customerId = customerFilter.customerId;
      }
      const queryString = new URLSearchParams(params).toString();
      console.log('[RecordingRules] Fetching rules with params:', params);
      const response = await api.get(`/endpoints/recording-rules?${queryString}`);
      
      if (response.data.success) {
        setRules(response.data.rules);
      } else {
        setError('Failed to load recording rules');
      }
    } catch (err: any) {
      console.error('Error fetching recording rules:', err);
      setError(err.response?.data?.message || 'Error loading recording rules');
    } finally {
      setLoading(false);
    }
  }, [customerFilter, isLoginAsCustomer, member?.customerID, waitingForLoginAs]);
  
  useEffect(() => {
    if (!waitingForLoginAs) {
      // Add small delay to ensure session storage is ready
      const timer = setTimeout(() => {
        fetchRules();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [fetchRules, waitingForLoginAs]);
  
  // Listen for login-as-customer changes
  useEffect(() => {
    const handleLoginAsChange = () => {
      console.log('[RecordingRules] Login-as-customer changed, refreshing data');
      fetchRules();
    };
    
    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange);
    return () => window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange);
  }, [fetchRules]);
  
  // Update browser tab title with customer info
  useEffect(() => {
    if (customerFilter.isFiltered && customerFilter.customerId && customerFilter.customerName) {
      document.title = `${customerFilter.customerId} - ${customerFilter.customerName} - Recording Rules`;
    } else {
      document.title = 'Recording Rules - Admin Portal';
    }
  }, [customerFilter]);
  
  const handleOpenDialog = (rule?: RecordingRule) => {
    if (rule) {
      setEditMode(true);
      setSelectedRule(rule);
      setFormData({
        rule_name: rule.rule_name,
        rule_type: rule.rule_type,
        rule_value: rule.rule_value || '',
        action: rule.action,
        priority: rule.priority,
        is_active: rule.is_active
      });
    } else {
      setEditMode(false);
      setSelectedRule(null);
      setFormData({
        rule_name: '',
        rule_type: 'global',
        rule_value: '',
        action: 'record',
        priority: 100,
        is_active: true
      });
    }
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedRule(null);
  };
  
  const handleSave = async () => {
    try {
      // Validate
      if (!formData.rule_name.trim()) {
        setSnackbar({ 
          open: true, 
          message: 'Rule name is required', 
          severity: 'error' 
        });
        return;
      }
      
      if (formData.rule_type !== 'global' && !formData.rule_value.trim()) {
        setSnackbar({ 
          open: true, 
          message: `${formData.rule_type} value is required`, 
          severity: 'error' 
        });
        return;
      }
      
      const data = {
        ...formData,
        rule_value: formData.rule_type === 'global' ? null : formData.rule_value
      };
      
      // Build params for customer context
      const params: any = {};
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && customerFilter.customerId) {
        params.customerId = customerFilter.customerId;
      }

      if (editMode && selectedRule) {
        await api.put(`/endpoints/recording-rules/${selectedRule.id}`, data, { params });
        setSnackbar({
          open: true,
          message: 'Recording rule updated successfully',
          severity: 'success'
        });
      } else {
        await api.post('/endpoints/recording-rules', data, { params });
        setSnackbar({ 
          open: true, 
          message: 'Recording rule created successfully', 
          severity: 'success' 
        });
      }
      
      handleCloseDialog();
      fetchRules();
    } catch (err: any) {
      console.error('Error saving recording rule:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Failed to save recording rule', 
        severity: 'error' 
      });
    }
  };
  
  const handleDelete = async () => {
    if (!selectedRule) return;

    try {
      // Build params for customer context
      const params: any = {};
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && customerFilter.customerId) {
        params.customerId = customerFilter.customerId;
      }

      await api.delete(`/endpoints/recording-rules/${selectedRule.id}`, { params });
      setSnackbar({ 
        open: true, 
        message: 'Recording rule deleted successfully', 
        severity: 'success' 
      });
      setDeleteDialog(false);
      setSelectedRule(null);
      fetchRules();
    } catch (err: any) {
      console.error('Error deleting recording rule:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Failed to delete recording rule', 
        severity: 'error' 
      });
    }
  };
  
  const handleToggleActive = async (rule: RecordingRule) => {
    try {
      await api.put(`/endpoints/recording-rules/${rule.id}`, {
        is_active: !rule.is_active
      });
      fetchRules();
    } catch (err: any) {
      console.error('Error toggling rule status:', err);
      setSnackbar({ 
        open: true, 
        message: 'Failed to update rule status', 
        severity: 'error' 
      });
    }
  };
  
  const getRuleTypeLabel = (type: string) => {
    switch (type) {
      case 'global':
        return 'Global';
      case 'did':
        return 'Phone Number';
      case 'extension':
        return 'Extension';
      case 'direction':
        return 'Call Direction';
      default:
        return type;
    }
  };
  
  const getRuleValueDisplay = (rule: RecordingRule) => {
    if (rule.rule_type === 'global') {
      return <Typography variant="body2" color="textSecondary">All Calls</Typography>;
    }
    if (rule.rule_type === 'direction') {
      return <Chip label={rule.rule_value} size="small" variant="outlined" />;
    }
    return <Typography variant="body2">{rule.rule_value}</Typography>;
  };
  
  const getActionIcon = (action: string) => {
    return action === 'record' ? 
      <MicIcon color="error" fontSize="small" /> : 
      <MicOffIcon color="disabled" fontSize="small" />;
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">Recording Rules</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Rule
            </Button>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Recording rules are evaluated in priority order (lowest number first). 
              The first matching rule determines whether a call is recorded.
            </Typography>
          </Alert>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={40}></TableCell>
                  <TableCell>Rule Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Usage</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="textSecondary" py={4}>
                        No recording rules configured. Add a rule to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <DragIcon color="disabled" fontSize="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {rule.rule_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Created by {rule.created_by}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getRuleTypeLabel(rule.rule_type)} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{getRuleValueDisplay(rule)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {getActionIcon(rule.action)}
                          <Typography variant="body2">
                            {rule.action === 'record' ? 'Record' : "Don't Record"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={rule.priority} size="small" />
                      </TableCell>
                      <TableCell>
                        {rule.usage_count ? (
                          <Tooltip title={rule.last_used ? `Last used: ${format(new Date(rule.last_used), 'MMM dd, yyyy')}` : ''}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <HistoryIcon fontSize="small" color="action" />
                              <Typography variant="caption">
                                {rule.usage_count}
                              </Typography>
                            </Stack>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            Not used
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={rule.is_active}
                          onChange={() => handleToggleActive(rule)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(rule)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedRule(rule);
                            setDeleteDialog(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      
      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Recording Rule' : 'Add Recording Rule'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Rule Name"
              value={formData.rule_name}
              onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
              margin="normal"
              helperText="A descriptive name for this rule"
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Rule Type</InputLabel>
              <Select
                value={formData.rule_type}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  rule_type: e.target.value as any,
                  rule_value: '' // Clear value when type changes
                })}
                label="Rule Type"
              >
                <MenuItem value="global">Global (All Calls)</MenuItem>
                <MenuItem value="did">Specific Phone Number</MenuItem>
                <MenuItem value="extension">Specific Extension</MenuItem>
                <MenuItem value="direction">Call Direction</MenuItem>
              </Select>
            </FormControl>
            
            {formData.rule_type !== 'global' && (
              <>
                {formData.rule_type === 'direction' ? (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Call Direction</InputLabel>
                    <Select
                      value={formData.rule_value}
                      onChange={(e) => setFormData({ ...formData, rule_value: e.target.value })}
                      label="Call Direction"
                    >
                      <MenuItem value="inbound">Inbound</MenuItem>
                      <MenuItem value="outbound">Outbound</MenuItem>
                      <MenuItem value="internal">Internal</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label={formData.rule_type === 'did' ? 'Phone Number' : 'Extension'}
                    value={formData.rule_value}
                    onChange={(e) => setFormData({ ...formData, rule_value: e.target.value })}
                    margin="normal"
                    helperText={
                      formData.rule_type === 'did' 
                        ? 'Enter the 10-digit phone number' 
                        : 'Enter the extension number'
                    }
                  />
                )}
              </>
            )}
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Action</InputLabel>
              <Select
                value={formData.action}
                onChange={(e) => setFormData({ ...formData, action: e.target.value as any })}
                label="Action"
              >
                <MenuItem value="record">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MicIcon fontSize="small" />
                    <span>Record</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="no_record">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MicOffIcon fontSize="small" />
                    <span>Don't Record</span>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              type="number"
              label="Priority"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 100 })}
              margin="normal"
              helperText="Lower numbers have higher priority"
              inputProps={{ min: 1, max: 999 }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Recording Rule</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the rule "{selectedRule?.rule_name}"?
          </Typography>
          {selectedRule?.usage_count && selectedRule.usage_count > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This rule has been used {selectedRule.usage_count} times.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}